<template>
  <div class="min-h-screen bg-gray-50 py-12">
    <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      <!-- Breadcrumb -->
      <nav class="mb-8">
        <ol class="flex flex-wrap items-center space-x-2 text-sm text-gray-500">
          <li><router-link to="/" class="hover:text-blue-600">Trang chủ</router-link></li>
          <li>/</li>
          <li><router-link to="/blog" class="hover:text-blue-600">Blog</router-link></li>
          <li>/</li>
          <li class="text-gray-900">Core Web Vitals 2025</li>
        </ol>
      </nav>

      <!-- Article Header -->
      <header class="mb-8">
        <h1 class="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">Tối ưu Core Web Vitals 2025: Hướng dẫn LCP, CLS, INP từ A-Z</h1>
        <div class="flex items-center text-sm text-gray-500 mb-6">
          <span>—</span>
          <span class="mx-2">•</span>
          <span>9 phút đọc</span>
          <span class="mx-2">•</span>
          <span>Hiệu năng & SEO</span>
        </div>
        <div class="h-48 sm:h-56 lg:h-64 bg-gradient-to-br from-amber-500 to-orange-600 rounded-lg flex items-center justify-center">
          <svg class="w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
          </svg>
        </div>
      </header>

      <!-- Article Content -->
      <article class="bg-white rounded-lg shadow-lg p-4 sm:p-6 lg:p-8">
        <div class="prose prose-lg max-w-none">
          <h2>Core Web Vitals là gì?</h2>
          <p>Core Web Vitals là bộ chỉ số quan trọng nhất của Google để đánh giá trải nghiệm người dùng trên website. Năm 2025, Google vẫn tập trung vào 3 chỉ số chính:</p>
          
          <div class="bg-blue-50 p-4 sm:p-6 rounded-lg my-6">
            <h3 class="text-xl font-semibold mb-4">3 Chỉ số Core Web Vitals 2025</h3>
            <ul class="space-y-3">
              <li><strong>LCP (Largest Contentful Paint)</strong> – Thời gian tải nội dung lớn nhất (mục tiêu ≤ 2.5s)</li>
              <li><strong>CLS (Cumulative Layout Shift)</strong> – Độ ổn định layout (mục tiêu ≤ 0.1)</li>
              <li><strong>INP (Interaction to Next Paint)</strong> – Thời gian phản hồi tương tác (mục tiêu ≤ 200ms)</li>
            </ul>
          </div>

          <h2>1. Tối ưu LCP (Largest Contentful Paint)</h2>
          <p>LCP đo thời gian tải nội dung lớn nhất trên trang. Đây thường là hình ảnh hero, video, hoặc khối text lớn.</p>
          
          <h3>Các bước tối ưu LCP:</h3>
          <ol>
            <li><strong>Preload critical resources:</strong>
              <pre class="bg-gray-900 text-green-400 p-3 sm:p-4 rounded-lg my-4 overflow-x-auto text-xs sm:text-sm"><code>&lt;link rel="preload" href="hero-image.jpg" as="image"&gt;
&lt;link rel="preload" href="critical.css" as="style"&gt;</code></pre>
            </li>
            <li><strong>Optimize images:</strong> Sử dụng WebP, AVIF, lazy loading
              <pre class="bg-gray-900 text-green-400 p-3 sm:p-4 rounded-lg my-4 overflow-x-auto text-xs sm:text-sm"><code>&lt;img src="image.webp" loading="lazy" alt="description"&gt;</code></pre>
            </li>
            <li><strong>Minimize render-blocking CSS:</strong> Inline critical CSS, defer non-critical</li>
            <li><strong>Use CDN:</strong> Cloudflare, AWS CloudFront để tăng tốc độ tải</li>
          </ol>

          <h2>2. Tối ưu CLS (Cumulative Layout Shift)</h2>
          <p>CLS đo độ ổn định của layout. Layout shift xảy ra khi các element thay đổi vị trí đột ngột.</p>
          
          <h3>Nguyên nhân gây CLS:</h3>
          <ul>
            <li>Hình ảnh không có kích thước cố định</li>
            <li>Fonts load muộn (FOUT/FOIT)</li>
            <li>Ads, embeds load không đồng bộ</li>
            <li>Dynamic content injection</li>
          </ul>

          <h3>Giải pháp:</h3>
          <ol>
            <li><strong>Đặt kích thước cho tất cả media:</strong>
              <pre class="bg-gray-900 text-green-400 p-3 sm:p-4 rounded-lg my-4 overflow-x-auto text-xs sm:text-sm"><code>&lt;img src="image.jpg" width="800" height="600" alt="description"&gt;</code></pre>
            </li>
            <li><strong>Preload fonts:</strong>
              <pre class="bg-gray-900 text-green-400 p-3 sm:p-4 rounded-lg my-4 overflow-x-auto text-xs sm:text-sm"><code>&lt;link rel="preload" href="font.woff2" as="font" type="font/woff2" crossorigin&gt;</code></pre>
            </li>
            <li><strong>Reserve space cho ads:</strong>
              <pre class="bg-gray-900 text-green-400 p-3 sm:p-4 rounded-lg my-4 overflow-x-auto text-xs sm:text-sm"><code>&lt;div style="min-height: 250px;"&gt;&lt;!-- Ad placeholder --&gt;&lt;/div&gt;</code></pre>
            </li>
          </ol>

          <h2>3. Tối ưu INP (Interaction to Next Paint)</h2>
          <p>INP thay thế FID từ 2024, đo thời gian từ khi user tương tác đến khi browser có thể render frame tiếp theo.</p>
          
          <h3>Nguyên nhân INP kém:</h3>
          <ul>
            <li>JavaScript blocking quá lâu</li>
            <li>Event handlers phức tạp</li>
            <li>Long tasks (>50ms)</li>
            <li>Main thread bị block</li>
          </ul>

          <h3>Giải pháp tối ưu INP:</h3>
          <ol>
            <li><strong>Code splitting:</strong> Chia nhỏ JavaScript
              <pre class="bg-gray-900 text-green-400 p-3 sm:p-4 rounded-lg my-4 overflow-x-auto text-xs sm:text-sm"><code>const module = await import('./heavy-module.js');</code></pre>
            </li>
            <li><strong>Debounce/Throttle:</strong> Giới hạn tần suất thực thi
              <pre class="bg-gray-900 text-green-400 p-3 sm:p-4 rounded-lg my-4 overflow-x-auto text-xs sm:text-sm"><code>const debounced = debounce(handleInput, 300);</code></pre>
            </li>
            <li><strong>Web Workers:</strong> Chuyển heavy tasks ra background
              <pre class="bg-gray-900 text-green-400 p-3 sm:p-4 rounded-lg my-4 overflow-x-auto text-xs sm:text-sm"><code>const worker = new Worker('heavy-task.js');</code></pre>
            </li>
            <li><strong>Optimize event handlers:</strong> Tránh DOM manipulation trong loops</li>
          </ol>

          <h2>4. Công cụ đo lường Core Web Vitals</h2>
          <div class="grid md:grid-cols-2 gap-6 my-6">
            <div class="bg-green-50 p-4 rounded-lg">
              <h3 class="font-semibold text-green-800 mb-2">Lab Tools</h3>
              <ul class="text-sm text-green-700">
                <li>• Google PageSpeed Insights</li>
                <li>• Chrome DevTools Lighthouse</li>
                <li>• WebPageTest.org</li>
                <li>• GTmetrix</li>
              </ul>
            </div>
            <div class="bg-blue-50 p-4 rounded-lg">
              <h3 class="font-semibold text-blue-800 mb-2">Field Tools</h3>
              <ul class="text-sm text-blue-700">
                <li>• Chrome UX Report</li>
                <li>• Google Search Console</li>
                <li>• Real User Monitoring</li>
                <li>• Core Web Vitals API</li>
              </ul>
            </div>
          </div>

          <h2>5. Checklist tối ưu nhanh</h2>
          <div class="bg-yellow-50 p-6 rounded-lg">
            <h3 class="font-semibold mb-4">✅ Checklist Core Web Vitals</h3>
            <div class="grid md:grid-cols-2 gap-4">
              <div>
                <h4 class="font-medium">LCP Optimization:</h4>
                <ul class="text-sm space-y-1">
                  <li>□ Preload critical resources</li>
                  <li>□ Optimize images (WebP/AVIF)</li>
                  <li>□ Use CDN</li>
                  <li>□ Minimize render-blocking CSS</li>
                </ul>
              </div>
              <div>
                <h4 class="font-medium">CLS Prevention:</h4>
                <ul class="text-sm space-y-1">
                  <li>□ Set image dimensions</li>
                  <li>□ Preload fonts</li>
                  <li>□ Reserve ad space</li>
                  <li>□ Avoid dynamic content injection</li>
                </ul>
              </div>
              <div>
                <h4 class="font-medium">INP Improvement:</h4>
                <ul class="text-sm space-y-1">
                  <li>□ Code splitting</li>
                  <li>□ Debounce/throttle events</li>
                  <li>□ Use Web Workers</li>
                  <li>□ Optimize event handlers</li>
                </ul>
              </div>
              <div>
                <h4 class="font-medium">General:</h4>
                <ul class="text-sm space-y-1">
                  <li>□ Enable compression (Brotli)</li>
                  <li>□ Use HTTP/2 or HTTP/3</li>
                  <li>□ Minimize third-party scripts</li>
                  <li>□ Regular performance audits</li>
                </ul>
              </div>
            </div>
          </div>

          <h2>6. Monitoring và Maintenance</h2>
          <p>Core Web Vitals cần được theo dõi liên tục vì chúng thay đổi theo thời gian:</p>
          <ul>
            <li><strong>Set up monitoring:</strong> Sử dụng Google Search Console để theo dõi field data</li>
            <li><strong>Regular audits:</strong> Chạy Lighthouse mỗi tuần</li>
            <li><strong>Performance budgets:</strong> Đặt giới hạn cho bundle size, số requests</li>
            <li><strong>User feedback:</strong> Thu thập feedback về trải nghiệm thực tế</li>
          </ul>

          <div class="bg-gray-50 p-6 rounded-lg mt-8">
            <h3 class="font-semibold mb-3">💡 Lời khuyên chuyên gia</h3>
            <p class="text-sm text-gray-700">Core Web Vitals không chỉ ảnh hưởng đến SEO mà còn tác động trực tiếp đến conversion rate. Website có Core Web Vitals tốt thường có tỷ lệ bounce rate thấp hơn 40% và conversion rate cao hơn 20%.</p>
          </div>
        </div>
      </article>

      <!-- Back Button -->
      <div class="mt-8 text-center">
        <router-link 
          to="/blog" 
          class="inline-flex items-center px-4 sm:px-6 py-2 sm:py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors duration-200 text-sm sm:text-base"
        >
          <svg class="mr-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
          </svg>
          Quay lại Blog
        </router-link>
      </div>
    </div>
  </div>
</template>

<script setup>
import { useHead } from '@vueuse/head'
const title = 'Tối ưu Core Web Vitals 2025: Hướng dẫn LCP, CLS, INP từ A-Z'
const desc  = 'Hướng dẫn chi tiết tối ưu Core Web Vitals 2025: LCP, CLS, INP. Công cụ đo lường, checklist và best practices cho website'
const url   = 'https://webservices.vn/blog/core-web-vitals-2025'

useHead({
  title,
  meta: [
    { name: 'description', content: desc },
    { property: 'og:type', content: 'article' },
    { property: 'og:title', content: title },
    { property: 'og:description', content: desc },
    { property: 'og:url', content: url },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: title },
    { name: 'twitter:description', content: desc }
  ],
  link: [{ rel: 'canonical', href: url }]
})
</script>
